import { useCallback, useEffect, useRef, useState } from "react";

const LERP = 0.14;

function canUseGlow() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (window.matchMedia("(pointer: coarse)").matches) return false;
  return true;
}

/** Zone ambient glow — smooth liquid follow (desktop pointer only). */
export default function useLiquidGlow() {
  const [visible, setVisible] = useState(false);
  const target = useRef({ x: 0, y: 0 });
  const smooth = useRef({ x: 0, y: 0 });
  const raf = useRef(0);
  const zoneRef = useRef(null);
  const enabled = useRef(canUseGlow());

  const paint = useCallback(() => {
    const el = zoneRef.current;
    if (!el || !enabled.current) return;

    smooth.current.x += (target.current.x - smooth.current.x) * LERP;
    smooth.current.y += (target.current.y - smooth.current.y) * LERP;

    const rect = el.getBoundingClientRect();
    const px = ((smooth.current.x - rect.left) / rect.width) * 100;
    const py = ((smooth.current.y - rect.top) / rect.height) * 100;

    el.style.setProperty("--lg-x", `${px}%`);
    el.style.setProperty("--lg-y", `${py}%`);

    el.querySelectorAll(".liquid-glass-glow").forEach((g) => {
      g.style.left = `${px}%`;
      g.style.top = `${py}%`;
    });

    const dx = target.current.x - smooth.current.x;
    const dy = target.current.y - smooth.current.y;
    if (Math.abs(dx) > 0.35 || Math.abs(dy) > 0.35) {
      raf.current = requestAnimationFrame(paint);
    } else {
      raf.current = 0;
    }
  }, []);

  const queue = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(paint);
  }, [paint]);

  const onMove = useCallback(
    (e) => {
      if (!enabled.current) return;
      target.current = { x: e.clientX, y: e.clientY };
      setVisible(true);
      queue();
    },
    [queue]
  );

  const onLeave = useCallback(() => {
    setVisible(false);
    const el = zoneRef.current;
    if (el) {
      el.style.setProperty("--lg-x", "50%");
      el.style.setProperty("--lg-y", "50%");
    }
  }, []);

  useEffect(
    () => () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    },
    []
  );

  return { visible: enabled.current && visible, onMove, onLeave, zoneRef };
}
